import { CustomLoader } from "@admin/components/ui/custom-loader";
import { useState, useEffect, useCallback, useMemo } from "react";
import { Card, CardContent } from "../../components/ui/card";
import { DataTable, type TableColumnDef } from "../../components/data-table";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Plus, Edit, Trash2, Eye, Quote } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import {
  testimonialsAPI,
  type Testimonial,
  type TestimonialCreatePayload,
  PaginationParams,
} from "../../services/api";
import { CommonPagination } from "../../components/CommonPagination";

const STATIC_CATEGORIES = [
  "Student",
  "Parent",
  "Trustee / Volunteer",
  "Alumni",
  "Donor",
];

export default function TestimonialsManagement() {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Form state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<TestimonialCreatePayload>({
    category: STATIC_CATEGORIES[0],
    name: "",
    designation: "",
    description: "",
    is_enabled: true,
    order_by: 0,
  });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [editingItem, setEditingItem] = useState<Testimonial | null>(null);
  const [viewingItem, setViewingItem] = useState<Testimonial | null>(null);
  const [deletingItem, setDeletingItem] = useState<Testimonial | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchTestimonials = useCallback(async () => {
    setLoading(true);
    try {
      const params: PaginationParams = {
        page: currentPage,
        page_size: pageSize,
      };
      const res = await testimonialsAPI.list(params);
      if (res && typeof res === "object") {
        const list = res.results ?? res.data ?? [];
        setTestimonials(Array.isArray(list) ? list : []);
        const count =
          res.count ??
          (res as any).total_count ??
          (Array.isArray(list) ? list.length : 0);
        setTotalItems(count);
        setTotalPages(Math.ceil(count / pageSize));
      }
    } catch {
      toast.error("Failed to load testimonials");
      setTestimonials([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize]);

  useEffect(() => {
    fetchTestimonials();
  }, [fetchTestimonials]);

  const resetForm = () => {
    setForm({
      category: STATIC_CATEGORIES[0],
      name: "",
      designation: "",
      description: "",
      is_enabled: true,
      order_by: 0,
    });
    setEditingItem(null);
  };

  const handleOpenCreate = () => {
    resetForm();
    setDialogOpen(true);
  };

  const handleOpenEdit = (item: Testimonial) => {
    setEditingItem(item);
    setForm({
      category: item.category,
      name: item.name,
      designation: item.designation,
      description: item.description,
      is_enabled: item.is_enabled,
      order_by: item.order_by,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!form.description.trim()) {
      toast.error("Description is required");
      return;
    }
    setSubmitLoading(true);
    try {
      if (editingItem) {
        await testimonialsAPI.update(editingItem.id, form);
        toast.success("Testimonial updated successfully");
      } else {
        await testimonialsAPI.create(form);
        toast.success("Testimonial created successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchTestimonials();
    } catch (error: any) {
      toast.error(error?.message ?? "Failed to save testimonial");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingItem) {
      return;
    }
    setDeleteLoading(true);
    try {
      await testimonialsAPI.delete(deletingItem.id);
      toast.success("Testimonial deleted successfully");
      setDeletingItem(null);
      fetchTestimonials();
    } catch (error: any) {
      toast.error(error?.message ?? "Failed to delete testimonial");
    } finally {
      setDeleteLoading(false);
    }
  };

  const tableColumns = useMemo<TableColumnDef<Testimonial>[]>(
    () => [
      {
        id: "order",
        label: "Order",
        width: 72,
        alwaysVisible: true,
        renderCell: (_row, rowIndex) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
      },
      {
        id: "name",
        label: "Name",
        width: 160,
        renderCell: item => <span className="font-medium">{item.name}</span>,
      },
      {
        id: "category",
        label: "Category",
        width: 140,
        renderCell: item => (
          <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
            {item.category}
          </span>
        ),
      },
      {
        id: "designation",
        label: "Designation",
        width: 140,
        renderCell: item => item.designation,
      },
      {
        id: "status",
        label: "Status",
        width: 100,
        renderCell: item => (
          <span
            className={
              item.is_enabled
                ? "font-medium text-green-600"
                : "text-muted-foreground"
            }
          >
            {item.is_enabled ? "Enabled" : "Disabled"}
          </span>
        ),
      },
      {
        id: "actions",
        label: "Actions",
        width: 150,
        alwaysVisible: true,
        renderCell: item => (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setViewingItem(item)}
              className="text-blue-600 hover:text-blue-700"
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleOpenEdit(item)}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setDeletingItem(item)}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ],
    [currentPage, pageSize]
  );

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            User Testimonials
          </h1>
          <p className="text-muted-foreground">
            Manage stories and feedback from our community
          </p>
        </div>
        <Button onClick={handleOpenCreate} className="gap-2">
          <Plus className="h-4 w-4" />
          Add Testimonial
        </Button>
      </div>

      <Card>
        <CardContent className="p-3 pb-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <CustomLoader size="md" />
            </div>
          ) : testimonials.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Quote className="mb-4 h-12 w-12 text-muted opacity-20" />
              <p className="text-muted-foreground">
                No testimonials found. Click &quot;Add Testimonial&quot; to
                create one.
              </p>
            </div>
          ) : (
            <DataTable
              title={`Testimonials (${totalItems})`}
              tableId="super-admin-testimonials-management"
              columns={tableColumns}
              data={testimonials}
              getRowKey={item => item.id}
            />
          )}

          {totalItems > 0 && (
            <div className="mt-4 border-t pt-4">
              <CommonPagination
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                totalCount={totalItems}
                onPageChange={setCurrentPage}
                onPageSizeChange={size => {
                  setPageSize(size);
                  setCurrentPage(1);
                }}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? "Edit Testimonial" : "Add New Testimonial"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="t-name">Name *</Label>
              <Input
                id="t-name"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. John Doe"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="t-category">Category *</Label>
                <Select
                  value={form.category}
                  onValueChange={v => setForm({ ...form, category: v })}
                >
                  <SelectTrigger id="t-category">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATIC_CATEGORIES.map(c => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="t-designation">Designation</Label>
                <Input
                  id="t-designation"
                  value={form.designation}
                  onChange={e =>
                    setForm({ ...form, designation: e.target.value })
                  }
                  placeholder="e.g. Student"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="t-description">Testimonial *</Label>
              <Textarea
                id="t-description"
                value={form.description}
                onChange={e =>
                  setForm({ ...form, description: e.target.value })
                }
                placeholder="Enter the feedback or story..."
                rows={5}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="t-order">Display Order</Label>
                <Input
                  id="t-order"
                  type="number"
                  value={form.order_by}
                  onChange={e =>
                    setForm({ ...form, order_by: Number(e.target.value) })
                  }
                />
              </div>
              <div className="flex items-center gap-2 pt-8">
                <Switch
                  id="t-enabled"
                  checked={form.is_enabled}
                  onCheckedChange={checked =>
                    setForm({ ...form, is_enabled: checked })
                  }
                />
                <Label htmlFor="t-enabled">Enabled</Label>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={submitLoading}>
                {submitLoading ? (
                  <CustomLoader size="xs" />
                ) : editingItem ? (
                  "Update"
                ) : (
                  "Create"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog
        open={!!viewingItem}
        onOpenChange={open => !open && setViewingItem(null)}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Testimonial Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pt-4 text-center">
            <div className="flex justify-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
                <Quote className="h-8 w-8 text-blue-600" />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold">{viewingItem?.name}</h3>
              <p className="text-sm text-muted-foreground">
                {viewingItem?.designation} • {viewingItem?.category}
              </p>
            </div>
            <div className="relative">
              <Quote className="absolute -left-2 -top-4 h-8 w-8 text-muted opacity-10" />
              <p className="px-6 text-lg italic leading-relaxed text-foreground">
                &quot;{viewingItem?.description}&quot;
              </p>
              <Quote className="absolute -bottom-4 -right-2 h-8 w-8 rotate-180 text-muted opacity-10" />
            </div>
            <div className="flex justify-center gap-4 border-t pt-4">
              <span
                className={
                  viewingItem?.is_enabled
                    ? "text-sm text-green-600"
                    : "text-sm text-muted-foreground"
                }
              >
                {viewingItem?.is_enabled
                  ? "Visible on site"
                  : "Hidden from site"}
              </span>
              <span className="text-sm text-muted-foreground">
                Order: {viewingItem?.order_by}
              </span>
            </div>
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => setViewingItem(null)}>
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog
        open={!!deletingItem}
        onOpenChange={open => !open && setDeletingItem(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Testimonial</DialogTitle>
          </DialogHeader>
          <p className="py-4 text-muted-foreground">
            Are you sure you want to delete the testimonial from{" "}
            <strong>{deletingItem?.name}</strong>? This action cannot be undone.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeletingItem(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteLoading}
            >
              {deleteLoading ? <CustomLoader size="xs" /> : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
